import { PageHeader, PortfolioSelection, PortfolioValue } from '@components/index';
import { Leaderboard } from '@mui/icons-material';
import { Box, Container } from '@mui/material';
import { ScreenState } from '@src/app/enums';
import { useAppDispatch, useAppSelector } from '@src/app/hooks/store';
import { setAsOfDate } from '@src/app/store/reducers/holdings';
import { getHoldingsAsync } from '@src/app/store/reducers/holdings/thunks';
import { FC, useEffect, useState } from 'react';
import { HoldingsDataGrid } from './HoldingsDataGrid';
import { useWindow } from '@src/app/hooks/useWindow';
import { selectHoldings } from '@src/app/store/reducers/holdings/selectors';
import { selectPortfolios } from '@src/app/store/reducers/portfolios/selectors';
import { useAppContext } from '@src/app/hooks/useAppContext';
import DateRangePicker from '@components/dateRange';
import { DateRange } from '@src/app/store';
import { LocalizationProvider } from '@mui/lab';
import AdapterLuxon from '@mui/lab/AdapterLuxon';

const Holdings: FC = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { resizing } = useWindow();
  const { transitioningMenu } = useAppContext();

  const [currentPageSize, setCurrentPageSize] = useState<number>(25);

  const { portfoliosSelected, portfolioInfoState } = useAppSelector(selectPortfolios);
  const { data, state, asOfDate, filters } = useAppSelector(selectHoldings);

  const setAsAtDate = (date: DateRange) => {
    dispatch(setAsOfDate(date));
  };

  const fetchHoldingsAsync = async () => {
    await dispatch(
      getHoldingsAsync({
        asOfDate: asOfDate.dateTo,
        clientPortfolios: portfoliosSelected,
      })
    );
  };

  useEffect(() => {
    if (state !== ScreenState.SuccessDataLoaded) {
      fetchHoldingsAsync();
    }
  }, []);

  useEffect(() => {
    if (state !== ScreenState.Loading) {
      fetchHoldingsAsync();
    }
  }, [asOfDate, portfoliosSelected]);

  return (
    <Container>
      <Box display="flex" flexDirection="column" gap={2} mb={6}>
        <PageHeader title="Holdings" icon={Leaderboard}>
          <PortfolioSelection allowCombining={false} disabled={state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading} />
        </PageHeader>
        <Box
          display="flex"
          sx={{
            alignItems: { lg: 'center', xl: 'center', md: 'center', sm: 'center', xs: 'flex-start' },
            flexDirection: {
              xl: 'row',
              lg: 'row',
              md: 'row',
              sm: 'row',
              xs: 'column',
            },
          }}
          mt={4}
          mb={4}
          gap={4}
        >
          <PortfolioValue totalValue={data?.totalValue ?? 0} />
          <LocalizationProvider dateAdapter={AdapterLuxon}>
            <DateRangePicker isDateOnly dateRange={asOfDate} setDateRange={(dateRange: DateRange) => setAsAtDate(dateRange)} />
          </LocalizationProvider>
        </Box>

        {!resizing && !transitioningMenu && (
          <HoldingsDataGrid
            isLoading={state === ScreenState.Loading || portfolioInfoState === ScreenState.Loading}
            filters={filters || []}
            asOfDate={asOfDate.dateTo}
            data={data}
            currentPageSize={currentPageSize}
            setCurrentPageSize={setCurrentPageSize}
          />
        )}
      </Box>
    </Container>
  );
};

export default Holdings;
